import { Composer } from "@somethings-ui/web";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import {
  isInlineBannerAction,
  type AttentionBannerDescriptor,
} from "./attention-banner";
import {
  BANNER_ACTION_ICON,
  BANNER_KIND_ICON,
} from "./attention-banner-icons";
import { AttentionBannerDetail } from "./attention-banner-panels";

interface AttentionBannerViewProps {
  descriptor: AttentionBannerDescriptor;
  open: boolean;
  onToggle?: () => void;
  onAction?: (actionId: string) => void;
  onInsertMagicMoment?: (text: string) => void;
}

export function AttentionBannerView({
  descriptor,
  open,
  onToggle,
  onAction,
  onInsertMagicMoment,
}: AttentionBannerViewProps) {
  const [selectedActionId, setSelectedActionId] = useState<string | null>(null);

  useEffect(() => {
    setSelectedActionId(null);
  }, [descriptor.kind]);

  const selectedAction =
    descriptor.actions.find((action) => action.id === selectedActionId) ?? null;
  const selectedInline =
    selectedAction && isInlineBannerAction(selectedAction)
      ? selectedAction
      : null;

  const handleAction = useCallback(
    (actionId: string) => {
      const action = descriptor.actions.find((item) => item.id === actionId);
      onAction?.(actionId);
      if (action && isInlineBannerAction(action)) {
        setSelectedActionId(actionId);
      }
    },
    [descriptor.actions, onAction],
  );

  const clearSelectedAction = useCallback(() => setSelectedActionId(null), []);

  return (
    <Composer.Banner
      title={descriptor.title}
      subtitle={selectedInline ? undefined : descriptor.subtitle}
      icon={BANNER_KIND_ICON[descriptor.kind]}
      open={open}
      onToggle={onToggle}
      toggleLabel={open ? "Collapse banner" : "Expand banner"}
    >
      {selectedInline ? (
        <AttentionBannerDetail
          action={selectedInline}
          teenGender={descriptor.teenGender}
          onBack={clearSelectedAction}
          onInsertMagicMoment={onInsertMagicMoment}
        />
      ) : (
        <>
          {descriptor.actions.map((action) => (
            <BannerChip
              key={action.id}
              onClick={() => handleAction(action.id)}
            >
              {action.icon ? BANNER_ACTION_ICON[action.icon] : null}
              {action.label}
            </BannerChip>
          ))}
        </>
      )}
    </Composer.Banner>
  );
}

function BannerChip({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded-[88px] p-2 font-body text-body-2 leading-5 text-text-secondary transition-colors hover:bg-surfaces-z0 hover:text-text-primary focus-visible:outline-none"
    >
      {children}
    </button>
  );
}
